/**
 * EmbedSdkDemo — live demonstration of `@inzara/embed-react` inside
 * the Developer Portal. The developer can:
 *   1. Pick a workspace (mgmt-key scoped).
 *   2. Mint a fresh embed session via `/api/mgmt/v1/embed/sessions`.
 *   3. See the resulting iframe rendered by <InzaraEmbed /> (which
 *      lives in `/app/sdk/embed-react`).
 *   4. Watch live postMessage events streaming below the iframe.
 *
 * The mgmt API key comes from the Developer Portal's auth strip — it
 * is only sent to INZARA's own /api/mgmt/v1 endpoints from this
 * component, never to third parties. The demo mirrors what a host's
 * backend would do in production (browser code should NEVER hold the
 * key in a real integration; this is a dev-tool convenience).
 */
import React, { useMemo, useState } from 'react';
import { Package, Play, Loader2, Copy, Check, AlertTriangle, MessageSquare } from 'lucide-react';
import { InzaraEmbed } from '@/inzara-embed-react/index.js';
import { useBrand } from '@/lib/branding';

const RESOURCE_OPTIONS = [
  { value: 'insights',    label: 'Insights',    needsId: false },
  { value: 'kpi-monitor', label: 'KPI Monitor', needsId: false },
  { value: 'dashboard',   label: 'Dashboard',   needsId: true  },
  { value: 'report',      label: 'Report',      needsId: true  },
];

function CopyBtn({ text, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }
        catch { /* noop */ }
      }}
      className="text-[10px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
      data-testid={testid}
    >
      {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

export default function EmbedSdkDemo({ apiBase, bearerKey }) {
  const brand = useBrand();
  const [expanded,     setExpanded]     = useState(false);
  const [workspaceId,  setWorkspaceId]  = useState('');
  const [resource,     setResource]     = useState('insights');
  const [resourceId,   setResourceId]   = useState('');
  const [ttl,          setTtl]          = useState(300);
  const [minting,      setMinting]      = useState(false);
  const [token,        setToken]        = useState('');
  const [expiresAt,    setExpiresAt]    = useState(null);
  const [error,        setError]        = useState(null);
  const [events,       setEvents]       = useState([]);
  const [refreshCount, setRefreshCount] = useState(0);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);

  const needsId = useMemo(
    () => RESOURCE_OPTIONS.find(r => r.value === resource)?.needsId,
    [resource],
  );
  const host = apiBase; // INZARA is same-origin in this demo
  const canMint = bearerKey && workspaceId && (!needsId || resourceId);

  async function mintSession() {
    if (!canMint) return;
    setMinting(true);
    setError(null);
    setToken('');
    setEvents([]);
    setRefreshCount(0);
    setLastRefreshAt(null);
    try {
      const res = await fetch(`${apiBase}/api/mgmt/v1/embed/sessions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${bearerKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          workspace_id: workspaceId,
          sub: 'developer-portal-demo',
          email: 'demo@developer-portal',
          roles: ['viewer'],
          resource,
          resource_id: needsId ? resourceId : null,
          ttl_seconds: ttl,
        }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b?.detail?.message || b?.detail || `HTTP ${res.status}`);
      }
      const body = await res.json();
      setToken(body.token || '');
      setExpiresAt(body.expires_at || null);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setMinting(false);
    }
  }

  function pushEvent(ev) {
    setEvents(prev => [
      { at: Date.now(), type: ev?.type || '?', payload: ev },
      ...prev.slice(0, 19), // keep last 20
    ]);
  }

  const snippet =
`import { ${brand.embedComponent} } from '${brand.packageScope}/embed-react';

<${brand.embedComponent}
  host="${host}"
  token={/* short-lived JWT from /api/mgmt/v1/embed/sessions */}
  resource="${resource}"${needsId ? `\n  id="${resourceId || '<id>'}"` : ''}
  height={640}
  onEvent={(ev) => console.log(ev)}
  onTokenRefresh={(next, meta) => saveForNextMount(next)}
/>`;

  return (
    <div className="rounded-lg border border-border/40 bg-muted/10 mb-6" data-testid="developers-sdk-demo">
      <button
        type="button"
        onClick={() => setExpanded(x => !x)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="developers-sdk-demo-toggle"
      >
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium">{brand.packageScope}/embed-react — live demo</span>
          <span className="text-[10px] text-muted-foreground">iframe + postMessage in one component</span>
        </div>
        <span className="text-[10px] text-muted-foreground">{expanded ? 'Hide' : 'Show'}</span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-border/40 pt-3">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2 text-xs">
            <label className="space-y-1">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Workspace id</span>
              <input
                value={workspaceId}
                onChange={(e) => setWorkspaceId(e.target.value)}
                placeholder="ws_…"
                spellCheck={false}
                className="w-full font-mono px-2 py-1.5 rounded border border-border/40 bg-background/60"
                data-testid="developers-sdk-demo-workspace"
              />
            </label>
            <label className="space-y-1">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Resource</span>
              <select
                value={resource}
                onChange={(e) => { setResource(e.target.value); setToken(''); setError(null); }}
                className="w-full px-2 py-1.5 rounded border border-border/40 bg-background/60"
                data-testid="developers-sdk-demo-resource"
              >
                {RESOURCE_OPTIONS.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </label>
            <label className={`space-y-1 ${needsId ? '' : 'opacity-40'}`}>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Resource id</span>
              <input
                value={resourceId}
                onChange={(e) => { setResourceId(e.target.value); setToken(''); }}
                placeholder={needsId ? 'dash_… / rpt_…' : 'not required'}
                disabled={!needsId}
                spellCheck={false}
                className="w-full font-mono px-2 py-1.5 rounded border border-border/40 bg-background/60"
                data-testid="developers-sdk-demo-resource-id"
              />
            </label>
            <label className="space-y-1">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">TTL (sec)</span>
              <select
                value={ttl}
                onChange={(e) => setTtl(Number(e.target.value))}
                className="w-full px-2 py-1.5 rounded border border-border/40 bg-background/60"
                data-testid="developers-sdk-demo-ttl"
              >
                <option value={70}>70 (test refresh)</option>
                <option value={300}>300 (5 min)</option>
                <option value={900}>900 (15 min)</option>
                <option value={3600}>3600 (60 min)</option>
              </select>
            </label>
            <div className="flex items-end">
              <button
                type="button"
                onClick={mintSession}
                disabled={!canMint || minting}
                className="w-full text-xs px-3 py-1.5 rounded bg-primary text-primary-foreground disabled:opacity-40 inline-flex items-center justify-center gap-1.5"
                data-testid="developers-sdk-demo-mint"
              >
                {minting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                {minting ? 'Minting…' : token ? 'Re-mint & reload' : 'Mint session & render'}
              </button>
            </div>
          </div>

          {!bearerKey && (
            <div className="text-[11px] text-amber-300 flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" /> Paste an API key above first — the demo mints against
              <code className="text-[10px] mx-1 px-1 rounded bg-muted/40">POST /api/mgmt/v1/embed/sessions</code>.
            </div>
          )}

          {error && (
            <div className="text-[11px] text-rose-300 border border-rose-500/30 rounded px-2 py-1.5" data-testid="developers-sdk-demo-error">
              {error}
            </div>
          )}

          {token && (
            <>
              <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                <span>
                  Token expires at {expiresAt ? new Date(expiresAt).toLocaleTimeString() : 'unknown'}. Never expose a mgmt key in a real host.
                </span>
                <span
                  data-testid="developers-sdk-demo-refresh-status"
                  className={`px-1.5 py-0.5 rounded border ${refreshCount > 0 ? 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10' : 'text-muted-foreground border-border/40'}`}
                >
                  {refreshCount > 0
                    ? `auto-refreshed ×${refreshCount}${lastRefreshAt ? ` @ ${new Date(lastRefreshAt).toLocaleTimeString()}` : ''}`
                    : 'awaiting auto-refresh…'}
                </span>
              </div>
              <div className="rounded border border-border/40 overflow-hidden bg-background" data-testid="developers-sdk-demo-frame">
                <InzaraEmbed
                  host={host}
                  token={token}
                  resource={resource}
                  id={needsId ? resourceId : undefined}
                  height={520}
                  onEvent={pushEvent}
                  onTokenRefresh={(_next, meta) => {
                    setRefreshCount((c) => c + 1);
                    setLastRefreshAt(Date.now());
                    setExpiresAt(meta?.expires_at || null);
                  }}
                  refreshLeadTimeSeconds={Math.max(15, Math.floor(ttl * 0.2))}
                  data-testid="developers-sdk-demo-iframe"
                />
              </div>

              <div className="rounded border border-border/40 bg-background/40 max-h-40 overflow-auto" data-testid="developers-sdk-demo-events">
                <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border/30 flex items-center gap-1">
                  <MessageSquare className="w-3 h-3" /> postMessage events
                  <span className="ml-auto text-[9px]">{events.length} received</span>
                </div>
                {events.length === 0 ? (
                  <div className="px-2 py-3 text-[10px] text-muted-foreground italic">Waiting for the embed to load…</div>
                ) : (
                  <ul className="text-[10px] font-mono divide-y divide-border/20">
                    {events.map((e, i) => (
                      <li key={i} className="px-2 py-1 flex items-baseline gap-2">
                        <span className="text-muted-foreground shrink-0 w-16">
                          {new Date(e.at).toLocaleTimeString([], { hour12: false })}
                        </span>
                        <span className="text-primary shrink-0">{e.type}</span>
                        <span className="text-muted-foreground truncate">
                          {JSON.stringify({ ...e.payload, type: undefined })}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}

          <div className="rounded border border-border/40 bg-background/60">
            <div className="flex items-center justify-between px-2 py-1 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border/30">
              <span>React snippet</span>
              <CopyBtn text={snippet} testid="developers-sdk-demo-copy-snippet" />
            </div>
            <pre className="text-[11px] px-3 py-2 overflow-x-auto font-mono">{snippet}</pre>
          </div>
        </div>
      )}
    </div>
  );
}
